import {HttpClient, HttpResponse} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {
  CategoryCode,
  ENVIRONMENT,
  EnvironmentShared,
  HealthAnswers,
  ProviderId,
  QuestionId,
  QuestionnaireId,
  QuestionnaireStatus,
  TextTranslations,
  UiQuestion,
  Version,
  WithOption,
} from '@emma-labs/shared/typescript';
import {Observable} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {defaultOptions, handleHttpError, responseToObject} from '../http-utils';

@Injectable({
  providedIn: 'root',
})
export class HealthQuestionnaireApiService {
  basePath: string;

  constructor(
    private readonly http: HttpClient,
    @Inject(ENVIRONMENT) readonly environment: EnvironmentShared,
  ) {
    this.basePath = `${environment.msProposalApiBase}/questionnaires`;
  }

  getQuestion(
    sessionId: string,
    providerId: ProviderId,
    version: Version,
    answers: HealthAnswers,
    questionId?: string,
  ): Observable<{
    status: QuestionnaireStatus;
    questionsLength: number;
    questionIndex: number;
    question: UiQuestion;
    categoryCode?: CategoryCode;
  }> {
    const body = answers;
    const options = defaultOptions(sessionId).buildOptions();
    const url = questionId
      ? new URL(`${this.basePath}/${providerId}/${version}/questions/${questionId}`)
      : new URL(`${this.basePath}/${providerId}/${version}/questions/current`);

    return this.http.post(url.toString(), body, options).pipe(
      map((response) =>
        responseToObject<{
          status: QuestionnaireStatus;
          questionsLength: number;
          questionIndex: number;
          question: UiQuestion;
          categoryCode?: CategoryCode;
        }>(response as HttpResponse<string>),
      ),
      catchError(handleHttpError),
    );
  }

  getQuestions(
    sessionId: string,
    providerId: ProviderId,
    version: Version,
    answers: HealthAnswers | undefined,
    withExtraQuestionnaires = false,
  ): Observable<{
    questions: UiQuestion[];
    status: QuestionnaireStatus;
    categoryCode?: CategoryCode;
    extraQuestionnaires?: {
      id: QuestionnaireId;
      questions: UiQuestion[];
      status: QuestionnaireStatus;
      title: TextTranslations;
    }[];
  }> {
    const body = answers;
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/${providerId}/${version}/questions`);
    if (withExtraQuestionnaires) {
      url.searchParams.set('withOptions', WithOption.EXTRA_QUESTIONNAIRES);
    }

    return this.http.post(url.toString(), body, options).pipe(
      map((response) =>
        responseToObject<{
          questions: UiQuestion[];
          status: QuestionnaireStatus;
          categoryCode: CategoryCode;
          extraQuestionnaires?: {
            id: QuestionnaireId;
            questions: UiQuestion[];
            status: QuestionnaireStatus;
            title: TextTranslations;
          }[];
        }>(response as HttpResponse<string>),
      ),
      catchError(handleHttpError),
    );
  }

  getCategoryCode(
    sessionId: string,
    providerId: ProviderId,
    version: Version,
    answers: HealthAnswers,
  ): Observable<CategoryCode | undefined> {
    const body = answers;
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/${providerId}/${version}/category`);

    return this.http.post(url.toString(), body, options).pipe(
      map((response) =>
        responseToObject<{
          status: QuestionnaireStatus;
          categoryCode?: CategoryCode;
        }>(response as HttpResponse<string>),
      ),
      map((statusAndCategoryCode) => statusAndCategoryCode.categoryCode),
      catchError(handleHttpError),
    );
  }

  getAllExtraQuestionnaires(
    id: string,
    providerId: ProviderId,
    version: Version,
  ): Observable<{
    extraQuestionnaires: {
      id: QuestionnaireId;
      questions: UiQuestion[];
      status: QuestionnaireStatus;
      title: TextTranslations;
    }[];
  }> {
    const options = defaultOptions(id).buildOptions();
    const url = new URL(`${this.basePath}/${providerId}/${version}/extra/all`);

    return this.http.get(url.toString(), options).pipe(
      map((response) =>
        responseToObject<{
          extraQuestionnaires: {
            id: QuestionnaireId;
            questions: UiQuestion[];
            status: QuestionnaireStatus;
            title: TextTranslations;
          }[];
        }>(response as HttpResponse<string>),
      ),
      catchError(handleHttpError),
    );
  }

  getExtraQuestionnaires(
    sessionId: string,
    providerId: ProviderId,
    version: Version,
    extraQuestionnaireConfigs: {
      questionnaireId: QuestionnaireId;
      healthAnswers: HealthAnswers;
      referenceQuestionId?: QuestionId;
    }[],
  ): Observable<{
    extraQuestionnaires: {
      id: QuestionnaireId;
      title: TextTranslations;
      questions: UiQuestion[];
      status: QuestionnaireStatus;
    }[];
  }> {
    const body = {extraQuestionnaireConfigs};
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/${providerId}/${version}/extra`);

    return this.http.post(url.toString(), body, options).pipe(
      map((response) =>
        responseToObject<{
          extraQuestionnaires: {
            id: QuestionnaireId;
            title: TextTranslations;
            questions: UiQuestion[];
            status: QuestionnaireStatus;
          }[];
        }>(response as HttpResponse<string>),
      ),
      catchError(handleHttpError),
    );
  }
}
